import { useEffect, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import { useRequest } from "../../hooks/useRequest"
import { appStore } from "../../store/appStore"
import { PaymentMethod, PaymentsProps } from "../../types/steps/buy"
import { Ticket } from "../../types/entities/ticket"
import Loader from "../../components/Loader"
import { InfoOrange, Sbp } from "../../components/svgCollection"
import ConfirmModal, { ConfirmModalHandle } from "../../components/ConfirmModal"
import { extractOrderHoldDates } from "../../lib/paymentHold"
import { isUnpaidOrderError } from "../../lib/unpaidOrderError"
import { saveOrderPaymentMethod } from "../../lib/orderPaymentMethodStorage"
import { isUnpaid } from "../tickets/ticketUtils"

const BODY = "'Manrope', sans-serif"
const MONO = "'JetBrains Mono', monospace"

export default function Payments({ payInfo, navigate, orderBody }: PaymentsProps) {
    const { request, requestData } = useRequest()
    const router = useNavigate()
    const { cinema, setError } = appStore()
    const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([])
    const [loading, setLoading] = useState(false)
    const confirmRef = useRef<ConfirmModalHandle>(null)

    const getPayments = async () => {
        try {
            setLoading(true)

            const base: PaymentMethod[] = [{ type: "card", label: "Банковская карта" }]

            const [sbp, sberpay] = await Promise.all([
                request("GET", `cinema/${cinema.id}/sbp`),
                request("GET", `cinema/${cinema.id}/sberpay`)
            ])

            if (sbp) base.unshift({ type: "sbp", label: "СБП" })
            if (sberpay) base.unshift({ type: "sberpay", label: "SberPay" })
            if (payInfo.privileges.pushkin_card) base.push({ type: "pushkin", label: "Пушкинская карта" })

            setPaymentMethods(base)
        } finally {
            setLoading(false)
        }
    }

    const redirectToUnpaidOrder = async () => {
        try {
            const ordersRes = await requestData<Ticket[]>({ type: "GET", path: "user/order", disableError: true })
            const unpaid =
                ordersRes.status === "success" && Array.isArray(ordersRes.data)
                    ? ordersRes.data.find(isUnpaid)
                    : undefined
            router("/tickets", { state: { highlightOrderId: unpaid?.id }, replace: true })
        } catch {
            router("/tickets", { replace: true })
        }
    }

    const selectPayment = async (type: PaymentMethod["type"]) => {
        try {
            setLoading(true)
            const resOrder = await requestData<{ id: string; created_at?: string; expired_at?: string }>({
                type: "POST",
                path: "user/order",
                sendData: { ...orderBody, bonus_pay: type === "pushkin" ? 0 : orderBody.bonus_pay },
                disableError: true,
            })

            if (resOrder.status === "error") {
                if (isUnpaidOrderError(resOrder.error.message)) {
                    await redirectToUnpaidOrder()
                    return
                }
                setError(resOrder.error.message)
                return
            }

            const order = resOrder.data
            const orderDates = extractOrderHoldDates(order)
            if (type === "pushkin") {
                saveOrderPaymentMethod(order.id, "pushkin_card")
                navigate("pushkin", { orderId: order.id, ...orderDates }, "replace")
                return
            }
            const resPayment = await request("POST", `user/order/${order.id}/payment`, { method: type })
            saveOrderPaymentMethod(order.id, type)
            if (type === "sbp") {
                navigate(
                    type,
                    {
                        orderNumber: resPayment.order_number,
                        orderId: order.id,
                        qrImage: resPayment.qrImage,
                        sbpBanks: resPayment.sbp_banks,
                        ...orderDates,
                    },
                    "replace",
                )
            } else {
                navigate(
                    "payStatus",
                    {
                        orderNumber: resPayment.order_number,
                        orderId: order.id,
                        paymentRedirectUrl: resPayment.redirect_url,
                        ...orderDates,
                    },
                    "replace",
                )
            }
        } finally {
            setLoading(false)
        }
    }

    const onInfoClick = () => {
        confirmRef.current?.open({
            title: "Правила отмены билета",
            description:
                "При оплате бонусами минимальная стоимость билета — 1 рубль. Отменить билет можно до начала сеанса через приложение или Личный кабинет на сайте."
        })
    }

    const aboutPushkin = () => {
        confirmRef.current?.open({
            title: "Важно!",
            description: "Оплата Пушкинской картой доступна только для заказа с ОДНИМ билетом. Вернитесь назад и выберите одно место."
        })
    }

    useEffect(() => {
        getPayments()
    }, [])

    if (loading) return <Loader />

    return (
        <>
            <ConfirmModal ref={confirmRef} />
            <div className="flex flex-col gap-2.5">
                {paymentMethods.map((item, index) => {
                    const isPushkinDisabled = item.type === "pushkin" && payInfo.selectedSeats.length > 1
                    return (
                        <button
                            key={index}
                            disabled={loading}
                            onClick={() => (isPushkinDisabled ? aboutPushkin() : selectPayment(item.type))}
                            className={`relative flex h-[50px] w-full cursor-pointer items-center justify-center gap-[5px] rounded-[14px] bg-white/15 text-[15px] text-white ${loading ? "opacity-50" : ""} p-2`}
                            style={{
                                ...(item.type === "sbp" ? { backgroundColor: "#F5F1E8", color: "#1a2d55" } : {}),
                                ...(isPushkinDisabled ? { opacity: 0.5 } : {}),
                                fontFamily: BODY,
                                fontWeight: 700,
                            }}>
                            {item.type === "pushkin" && <img src={require("../../assets/pushkin.png")} alt="" className="h-6" />}
                            {item.type === "sbp" ? <Sbp /> : item.label}
                            {isPushkinDisabled && (
                                <div className="absolute right-8">
                                    <InfoOrange />
                                </div>
                            )}
                        </button>
                    )
                })}
                <button
                    type="button"
                    onClick={onInfoClick}
                    className="cursor-pointer text-center text-[13px] text-[#349be6]"
                    style={{ fontFamily: MONO, fontWeight: 700 }}>
                    Правила отмены билета
                </button>
            </div>
        </>
    )
}
